import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Edit, RefreshCw, ArrowLeft, ChevronRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomerEditDialog = ({ customer, open, onOpenChange, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    gst_number: '',
    pan_number: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_designation: '',
    edition: 'standard',
    status: 'active',
    notes: '',
  });

  useEffect(() => {
    if (customer) {
      setForm({
        company_name: customer.company_name || '',
        address_line1: customer.address?.address_line1 || '',
        address_line2: customer.address?.address_line2 || '',
        city: customer.address?.city || '',
        state: customer.address?.state || '',
        pincode: customer.address?.pincode || '',
        country: customer.address?.country || 'India',
        gst_number: customer.gst_number || '',
        pan_number: customer.pan_number || '',
        contact_name: customer.contact_person?.name || '',
        contact_email: customer.contact_person?.email || '',
        contact_phone: customer.contact_person?.phone || '',
        contact_designation: customer.contact_person?.designation || '',
        edition: customer.edition || 'standard',
        status: customer.status || 'active',
        notes: customer.notes || '',
      });
      setStep(1);
    }
  }, [customer]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        company_name: form.company_name,
        address: {
          address_line1: form.address_line1,
          address_line2: form.address_line2 || null,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
          country: form.country,
        },
        gst_number: form.gst_number || null,
        pan_number: form.pan_number || null,
        contact_person: {
          name: form.contact_name,
          email: form.contact_email,
          phone: form.contact_phone,
          designation: form.contact_designation || null,
        },
        edition: form.edition,
        status: form.status,
        notes: form.notes || null,
      };

      await axios.put(`${API_URL}/oem/tenants/${customer.id}`, payload);
      toast.success('Customer updated successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateKey = async () => {
    if (customer.license?.is_activated) {
      toast.error('Cannot regenerate key for an already activated license');
      return;
    }
    
    setRegeneratingKey(true);
    try {
      const response = await axios.post(`${API_URL}/oem/tenants/${customer.id}/regenerate-key`);
      toast.success('New license key generated and email sent!');
      toast.info(`New Key: ${response.data.new_license_key}`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate key');
    } finally {
      setRegeneratingKey(false);
    }
  };

  const copyLicenseKey = () => {
    if (customer?.license?.license_key) {
      navigator.clipboard.writeText(customer.license.license_key);
      toast.success('License key copied!');
    }
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5" />
            Edit Customer - Step {step} of 3
          </DialogTitle>
          <DialogDescription>
            Site ID: <code className="bg-muted px-2 py-1 rounded">{customer.site_id}</code>
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicators */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${step === s ? 'bg-primary text-primary-foreground' : step > s ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                {step > s ? '✓' : s}
              </div>
              {s < 3 && <div className={`w-12 h-1 mx-1 ${step > s ? 'bg-green-500' : 'bg-muted'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Company Information */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Company Information</h3>
            
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input value={form.company_name} onChange={(e) => setForm(prev => ({ ...prev, company_name: e.target.value }))} data-testid="edit-company-name" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>GST Number</Label>
                <Input value={form.gst_number} onChange={(e) => setForm(prev => ({ ...prev, gst_number: e.target.value.toUpperCase() }))} maxLength={15} />
              </div>
              <div className="space-y-2">
                <Label>PAN Number</Label>
                <Input value={form.pan_number} onChange={(e) => setForm(prev => ({ ...prev, pan_number: e.target.value.toUpperCase() }))} maxLength={10} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Address Line 1 *</Label>
              <Input value={form.address_line1} onChange={(e) => setForm(prev => ({ ...prev, address_line1: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <Label>Address Line 2</Label>
              <Input value={form.address_line2} onChange={(e) => setForm(prev => ({ ...prev, address_line2: e.target.value }))} />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>City *</Label>
                <Input value={form.city} onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>State *</Label>
                <Input value={form.state} onChange={(e) => setForm(prev => ({ ...prev, state: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Pin Code *</Label>
                <Input value={form.pincode} onChange={(e) => setForm(prev => ({ ...prev, pincode: e.target.value }))} maxLength={6} />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Contact Person */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Contact Person</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={form.contact_name} onChange={(e) => setForm(prev => ({ ...prev, contact_name: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Designation</Label>
                <Input value={form.contact_designation} onChange={(e) => setForm(prev => ({ ...prev, contact_designation: e.target.value }))} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={form.contact_email} onChange={(e) => setForm(prev => ({ ...prev, contact_email: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm(prev => ({ ...prev, contact_phone: e.target.value }))} />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: License & Status */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-semibold">License & Status</h3>
            
            {/* License Key Display */}
            <Card className={customer.license?.is_activated ? 'border-green-500/30 bg-green-500/5' : 'border-amber-500/30 bg-amber-500/5'}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs text-muted-foreground">License Key</p>
                    <code className="text-lg font-mono font-bold tracking-wider">
                      {customer.license?.license_key || 'N/A'}
                    </code>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyLicenseKey}>
                      Copy
                    </Button>
                    {!customer.license?.is_activated && (
                      <Button variant="outline" size="sm" onClick={handleRegenerateKey} disabled={regeneratingKey}>
                        {regeneratingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        <span className="ml-1">Regenerate</span>
                      </Button>
                    )}
                  </div>
                </div>
                <Badge variant={customer.license?.is_activated ? 'default' : 'secondary'}>
                  {customer.license?.is_activated ? '✓ Activated' : '⏳ Pending Activation'}
                </Badge>
                {customer.license?.is_activated && customer.license?.activation_date && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Activated: {new Date(customer.license.activation_date).toLocaleString()}
                    {customer.license.activation_ip && ` | IP: ${customer.license.activation_ip}`}
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Product Edition</Label>
                <Select value={form.edition} onValueChange={(v) => setForm(prev => ({ ...prev, edition: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Current License Summary */}
            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Current License</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Creator Licenses: <span className="font-medium">{customer.license?.creator_licenses || 0}</span></div>
                  <div>Viewer Licenses: <span className="font-medium">{customer.license?.viewer_licenses || 0}</span></div>
                  <div>Valid Until: <span className="font-medium">{customer.license?.end_date ? new Date(customer.license.end_date).toLocaleDateString() : '-'}</span></div>
                  <div>Days Remaining: <span className="font-medium">{customer.license?.days_remaining || 0}</span></div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Use &quot;Add Licenses&quot; button to increase license counts or extend duration.</p>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Internal notes about this customer..." />
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {step < 3 ? (
              <Button onClick={() => setStep(step + 1)}>
                Next <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
